import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { UploadCloud, FileText, Loader2, Trash2, Download, Rocket } from "lucide-react";
import { toast } from "sonner";

const HEADERS = [
  "name", "source_host", "source_port", "source_email", "source_password", "source_ssl",
  "dest_host", "dest_port", "dest_email", "dest_password", "dest_ssl",
];

const SAMPLE =
  "name,source_host,source_port,source_email,source_password,source_ssl,dest_host,dest_port,dest_email,dest_password,dest_ssl\n" +
  "John Doe,imap.gmail.com,993,john@gmail.com,app-pass,true,mail.newdomain.com,993,john@newdomain.com,pass,true\n" +
  "Jane Roe,outlook.office365.com,993,jane@old.com,pass,true,mail.newdomain.com,993,jane@newdomain.com,pass,true";

function parseCSV(text) {
  const lines = text.trim().split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return [];
  const header = lines[0].split(",").map((h) => h.trim().toLowerCase());
  return lines.slice(1).map((line) => {
    const cells = line.split(",");
    const row = {};
    header.forEach((h, i) => (row[h] = (cells[i] || "").trim()));
    return {
      name: row.name || `${row.source_email} → ${row.dest_email}`,
      source_host: row.source_host, source_port: Number(row.source_port || 993),
      source_email: row.source_email, source_password: row.source_password,
      source_ssl: String(row.source_ssl).toLowerCase() !== "false",
      dest_host: row.dest_host, dest_port: Number(row.dest_port || 993),
      dest_email: row.dest_email, dest_password: row.dest_password,
      dest_ssl: String(row.dest_ssl).toLowerCase() !== "false",
    };
  }).filter((r) => r.source_host && r.source_email && r.dest_host && r.dest_email);
}

export default function BulkMigration() {
  const navigate = useNavigate();
  const inputRef = useRef();
  const [rows, setRows] = useState([]);
  const [drag, setDrag] = useState(false);
  const [running, setRunning] = useState(false);

  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const parsed = parseCSV(e.target.result);
      if (parsed.length === 0) {
        toast.error("No valid rows found. Check your CSV columns.");
        return;
      }
      if (parsed.length > 20) {
        toast.error("Maximum 20 mailboxes per bulk upload.");
        return;
      }
      setRows(parsed);
      toast.success(`Parsed ${parsed.length} mailbox(es)`);
    };
    reader.readAsText(file);
    if (inputRef.current) inputRef.current.value = "";
  };

  const execute = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/migrations/bulk", { migrations: rows, auto_start: true });
      toast.success(`Started ${data.created} migration(s)`);
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setRunning(false);
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([SAMPLE], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "mailshift-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Bulk</p>
          <h1 className="mt-1 font-heading text-4xl font-bold tracking-tighter">CSV Upload</h1>
          <p className="mt-2 text-muted-foreground">Migrate up to 20 mailboxes at once. Each row = one migration.</p>
        </div>
        <Button variant="outline" className="gap-2" onClick={downloadTemplate} data-testid="download-template-button">
          <Download className="h-4 w-4" /> Download template
        </Button>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); handleFile(e.dataTransfer.files[0]); }}
        onClick={() => inputRef.current?.click()}
        data-testid="csv-dropzone"
        className={`flex cursor-pointer flex-col items-center justify-center border-2 border-dashed p-12 text-center transition-colors duration-200 ${
          drag ? "border-primary bg-accent" : "border-border bg-card hover:border-primary"
        }`}
      >
        <UploadCloud className="h-10 w-10 text-primary" />
        <p className="mt-4 font-medium">Drop your CSV here, or click to browse</p>
        <p className="mt-1 font-mono text-xs text-muted-foreground">{HEADERS.join(", ")}</p>
        <input ref={inputRef} type="file" accept=".csv,text/csv" className="hidden"
               data-testid="csv-file-input" onChange={(e) => handleFile(e.target.files[0])} />
      </div>

      {rows.length > 0 && (
        <div className="mt-8 border border-border bg-card">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-6 py-4">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-primary" />
              <span className="font-semibold tracking-tight">{rows.length} mailbox(es) ready</span>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="gap-2" onClick={() => setRows([])} data-testid="clear-rows-button">
                <Trash2 className="h-4 w-4" /> Clear
              </Button>
              <Button size="sm" className="gap-2" onClick={execute} disabled={running} data-testid="execute-bulk-button">
                {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
                Start all migrations
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Destination</TableHead>
                  <TableHead>SSL</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={i} data-testid={`bulk-row-${i}`}>
                    <TableCell className="font-medium">{r.name}</TableCell>
                    <TableCell className="font-mono text-xs">{r.source_email}<br /><span className="text-muted-foreground">{r.source_host}:{r.source_port}</span></TableCell>
                    <TableCell className="font-mono text-xs">{r.dest_email}<br /><span className="text-muted-foreground">{r.dest_host}:{r.dest_port}</span></TableCell>
                    <TableCell className="font-mono text-xs">{r.source_ssl ? "on" : "off"} / {r.dest_ssl ? "on" : "off"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}
